import { api } from '../lib/api'

export type Listing = {
  content_warning?: {
    type: 'nudity' | 'violence' | 'sensitive'
    min_age: number
    requires_opt_in: boolean
    check_id?: string
    message: string
    viewer_allowed?: boolean
    blur_preview?: boolean
  } | null
  public_id: string
  active?: boolean
  is_public?: boolean
  is_removed?: boolean
  moderation_state?: 'ok' | 'pending_review' | 'requires_edit' | 'removed'
  user_safe_message?: string | null
  last_moderation_event_id?: string | null
  last_moderation_decision_action?: string | null
  price_pixe?: string | null
  product?: {
    title?: string
    subtitle?: string
    thumbnail_media?: {
      public_url?: string
      content_warning?: Listing['content_warning']
    } | null
  }
  images?: {
    public_id: string
    public_url?: string
    alt_text?: string
    content_warning?: Listing['content_warning']
  }[]
  share_url?: string
  likes_count?: number
  saves_count?: number
  stock_qty?: number | null
  reserved_qty?: number | null
  review_subject_id?: number
  condition?: 'new' | 'used' | null
  seller?: { name?: string; handle?: string } | null
  liked?: boolean
  is_saved?: boolean
}

export async function fetchListings(params?: Record<string, any>): Promise<Listing[]> {
  const { data } = await api.get<{ results: Listing[] }>(`/api/pixe_store/store/listings/`, { params })
  return Array.isArray(data?.results) ? data.results : []
}

// Detail payload extends listing fields with richer product info
export type ListingDetail = Listing & {
  product?: Listing["product"] & {
    description?: string | null
    category?: { name?: string } | null
    tags?: string[]
  }
}

export async function fetchListingDetail(publicId: string): Promise<ListingDetail> {
  const { data } = await api.get<ListingDetail>(`/api/pixe_store/store/listings/${publicId}/`)
  return data
}

// ---- Cart ----
export type CartItem = {
  id: number
  qty: number
  unit_price_pixe: string
  line_total_pixe: string
  listing: Listing
}
export type Cart = {
  public_id: string
  items: CartItem[]
  item_count: number
  subtotal_pixe: string
}

export async function getCart(): Promise<Cart> {
  const { data } = await api.get<Cart>('/api/pixe_store/store/cart/')
  return data
}

export async function addToCart(listing_public_id: string, qty = 1): Promise<{ ok: boolean; qty: number }> {
  const { data } = await api.post<{ ok: boolean; qty: number }>(`/api/pixe_store/store/cart/`, { listing_public_id, qty })
  return data
}

export async function removeFromCart(listing_public_id: string): Promise<{ ok: boolean }> {
  const { data } = await api.delete<{ ok: boolean }>(`/api/pixe_store/store/cart/`, { data: { listing_public_id } })
  return data
}

// ---- Checkout ----
export type Order = {
  public_id: string
  status: string
  grand_total_pixe: string
}

export async function createCheckout(listing_public_id: string, idempotency_key: string): Promise<Order> {
  const { data } = await api.post<Order>(`/api/pixe_store/store/checkout/`, { listing_public_id, idempotency_key })
  return data
}

export async function payOrder(order_public_id: string): Promise<{ ok: boolean; order: Order }> {
  const { data } = await api.post<{ ok: boolean; order: Order }>(`/api/pixe_store/store/checkout/${order_public_id}/pay/`)
  return data
}

// ---- Listing actions (save/like/share) ----
export async function saveListing(publicId: string): Promise<{ saved: boolean; saves: number; saved_at?: string | null }> {
  const { data } = await api.post<{ saved: boolean; saves: number; saved_at?: string | null }>(`/api/pixe_store/store/listings/${publicId}/save/`)
  return data
}
export async function unsaveListing(publicId: string): Promise<{ saved: boolean; saves: number }> {
  const { data } = await api.delete<{ saved: boolean; saves: number }>(`/api/pixe_store/store/listings/${publicId}/save/`)
  return data
}
export async function likeListing(publicId: string): Promise<{ liked: boolean; likes: number }> {
  const { data } = await api.post<{ liked: boolean; likes: number }>(`/api/pixe_store/store/listings/${publicId}/like/`)
  return data
}
export async function unlikeListing(publicId: string): Promise<{ liked: boolean; likes: number }> {
  const { data } = await api.delete<{ liked: boolean; likes: number }>(`/api/pixe_store/store/listings/${publicId}/like/`)
  return data
}
export async function shareListing(publicId: string): Promise<{ shared: boolean; shares: number; shared_at?: string }> {
  const { data } = await api.post<{ shared: boolean; shares: number; shared_at?: string }>(`/api/pixe_store/store/listings/${publicId}/share/`)
  return data
}

// ---- Create product + listing ----
export type CreateStoreProductInput = {
  title: string
  subtitle?: string
  description?: string
  product_type?: 'physical' | 'digital' | 'hybrid'
  category?: string | null
  tags?: string[]
  price_pixe: string | number
  compare_at_pixe?: string | number | null
  stock_qty?: number
  condition?: 'new' | 'used' | null
  image_url?: string | null
  active?: boolean
  gallery?: string[]
}

export async function createStoreProduct(payload: CreateStoreProductInput): Promise<{ product: any; listing: Listing }>{
  const { data } = await api.post<{ product: any; listing: Listing }>(`/api/pixe_store/store/products/`, payload)
  return data
}

export async function updateStoreListing(publicId: string, payload: Partial<CreateStoreProductInput>): Promise<{ product: any; listing: Listing }> {
  const { data } = await api.patch<{ product: any; listing: Listing }>(`/api/pixe_store/store/listings/${publicId}/`, payload)
  return data
}
